import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Alert, Box, Button, Card, CircularProgress, Typography } from "@mui/material";
import * as Sentry from "@sentry/react";
import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { type FormEvent, useEffect, useState } from "react";

import { createBookingSetupIntentFn } from "@/server-fns/booking/paymentFns";
import type { StoreData } from "@/server-fns/booking/query";
import { saveBookingByGuestFn } from "@/server-fns/booking/serverFns";
import { isBookingNotAvailableError, isUnknownError } from "@/types/errors";
import { computeCancellationFeeAmountCents } from "@/utils/cancellationFee";
import { payingGuests, totalGuests } from "@/utils/partySize";

import type { GuestBookingPayload } from "./BookingSummary";
import {
    clearPendingBooking,
    clearResumeQueryParams,
    getResumeSetupIntentClientSecret,
    persistPendingBooking,
    readPendingBooking,
} from "./pendingBookingStorage";

// Call outside the component, per Stripe.js guidance, so the Stripe object
// isn't recreated on every render.
const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY as string);

type Props = {
    payload: GuestBookingPayload;
    storeData: StoreData;
    captchaToken: string;
    onBack: () => void;
    onSuccess: (isAutoConfirmed: boolean) => void;
};

type SetupIntentInfo = {
    clientSecret: string;
    stripeCustomerId: string;
    stripeSetupIntentId: string;
};

export const PaymentStep = ({ payload, storeData, captchaToken, onBack, onSuccess }: Props) => {
    const [setupIntentInfo, setSetupIntentInfo] = useState<SetupIntentInfo | null>(null);
    const [loadError, setLoadError] = useState<string | null>(null);

    // Frozen at mount: if the guest just landed back here from a Stripe 3DS/SCA redirect,
    // return_url carries this query param. See pendingBookingStorage.ts for the full story.
    const [resumeClientSecret] = useState(() => getResumeSetupIntentClientSecret());
    const [resumePhase, setResumePhase] = useState<"checking" | "failed" | "done">(() =>
        resumeClientSecret ? "checking" : "done",
    );
    const [resumeError, setResumeError] = useState<string | null>(null);

    useEffect(() => {
        if (!resumeClientSecret) return;
        let cancelled = false;

        const resume = async () => {
            const stored = readPendingBooking();
            const stripe = await stripePromise;
            clearResumeQueryParams();

            if (!stored || !stripe) {
                clearPendingBooking();
                if (!cancelled) setResumePhase("done");
                return;
            }

            const { setupIntent } = await stripe.retrieveSetupIntent(resumeClientSecret);

            if (
                !setupIntent ||
                setupIntent.status !== "succeeded" ||
                typeof setupIntent.payment_method !== "string" ||
                !stored.stripeCustomerId
            ) {
                // Not a successful resume (declined, abandoned, still processing, etc.) — not
                // handling every SetupIntent status here. Fall back to a fresh PAYMENT step
                // using the recovered payload so the guest can retry without retyping their
                // booking details.
                clearPendingBooking();
                if (!cancelled) setResumePhase("done");
                return;
            }

            try {
                const result = await saveBookingByGuestFn({
                    data: {
                        payload: {
                            ...stored.payload,
                            stripeCustomerId: stored.stripeCustomerId,
                            stripePaymentMethodId: setupIntent.payment_method,
                            stripeSetupIntentId: setupIntent.id,
                        },
                        captchaToken: stored.captchaToken,
                    },
                });

                if (isBookingNotAvailableError(result)) {
                    clearPendingBooking();
                    if (!cancelled) {
                        setResumeError(
                            "Sorry, the booking is not available at the selected time anymore. Please start a new booking.",
                        );
                        setResumePhase("failed");
                    }
                    return;
                }

                if (isUnknownError(result)) {
                    clearPendingBooking();
                    if (!cancelled) {
                        setResumeError(
                            result?.message || "Apologies, the booking couldn’t be saved.",
                        );
                        setResumePhase("failed");
                    }
                    return;
                }

                clearPendingBooking();
                if (!cancelled) onSuccess(result.isAutoConfirmed);
            } catch (error) {
                Sentry.captureException(error);
                clearPendingBooking();
                if (!cancelled) {
                    setResumeError("Apologies, the booking couldn’t be saved due to an error.");
                    setResumePhase("failed");
                }
            }
        };

        resume().catch((error) => {
            Sentry.captureException(error);
            clearPendingBooking();
            if (!cancelled) {
                setResumeError("Apologies, the booking couldn’t be saved due to an error.");
                setResumePhase("failed");
            }
        });

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps -- resumeClientSecret is frozen at mount; onSuccess is stable for the lifetime of this step.
    }, [resumeClientSecret]);

    useEffect(() => {
        if (resumePhase !== "done") return;
        let cancelled = false;
        createBookingSetupIntentFn({
            data: { storeId: payload.storeId, email: payload.email },
        })
            .then((result) => {
                if (!cancelled) setSetupIntentInfo(result);
            })
            .catch((error) => {
                Sentry.captureException(error);
                if (!cancelled) {
                    setLoadError(
                        "We couldn’t set up the payment form. Please go back and try again.",
                    );
                }
            });
        return () => {
            cancelled = true;
        };
    }, [resumePhase, payload.storeId, payload.email]);

    if (resumePhase === "checking") {
        return (
            <Box
                component={Card}
                sx={{
                    p: { xs: 2, sm: 4 },
                    borderRadius: 2,
                    boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
                    border: "none",
                    bgcolor: "white",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 2,
                }}
            >
                <CircularProgress />
                <Typography color="text.secondary">Finalizing your booking...</Typography>
            </Box>
        );
    }

    if (resumePhase === "failed") {
        return (
            <Box
                component={Card}
                sx={{
                    p: { xs: 2, sm: 4 },
                    borderRadius: 2,
                    boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
                    border: "none",
                    bgcolor: "white",
                }}
            >
                <Typography color="error" sx={{ mb: 3 }}>
                    {resumeError}
                </Typography>
                <Button
                    variant="contained"
                    onClick={() => window.location.assign(window.location.pathname)}
                >
                    Start a new booking
                </Button>
            </Box>
        );
    }

    if (loadError) {
        return (
            <Box
                component={Card}
                sx={{
                    p: { xs: 2, sm: 4 },
                    borderRadius: 2,
                    boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
                    border: "none",
                    bgcolor: "white",
                }}
            >
                <Button
                    onClick={onBack}
                    startIcon={<ArrowBackIcon />}
                    sx={{ mb: 3, textTransform: "none", color: "text.secondary" }}
                >
                    Back to summary
                </Button>
                <Typography color="error">{loadError}</Typography>
            </Box>
        );
    }

    if (!setupIntentInfo) {
        return (
            <Box
                component={Card}
                sx={{
                    p: { xs: 2, sm: 4 },
                    borderRadius: 2,
                    boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
                    border: "none",
                    bgcolor: "white",
                    display: "flex",
                    justifyContent: "center",
                }}
            >
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Elements stripe={stripePromise} options={{ clientSecret: setupIntentInfo.clientSecret }}>
            <PaymentForm
                payload={payload}
                storeData={storeData}
                captchaToken={captchaToken}
                setupIntentInfo={setupIntentInfo}
                onBack={onBack}
                onSuccess={onSuccess}
            />
        </Elements>
    );
};

type PaymentFormProps = Props & { setupIntentInfo: SetupIntentInfo };

const PaymentForm = ({
    payload,
    storeData,
    captchaToken,
    setupIntentInfo,
    onBack,
    onSuccess,
}: PaymentFormProps) => {
    const stripe = useStripe();
    const elements = useElements();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [confirmedPaymentMethodId, setConfirmedPaymentMethodId] = useState<string | null>(null);

    const guestCount = payingGuests(payload);
    const feeType = storeData.bookingSettings?.cancellationFeeType ?? "PER_PERSON";
    const feeAmountCents = computeCancellationFeeAmountCents(
        feeType,
        storeData.bookingSettings?.cancellationFeeAmountCents ?? 0,
        guestCount,
    );
    const feeAmountDollars = (feeAmountCents / 100).toFixed(2);
    const freeCancellationUntilHours = storeData.bookingSettings?.freeCancellationUntilHours ?? 0;

    const handleSubmit = async (event: FormEvent) => {
        event.preventDefault();
        if (!stripe || !elements) return;

        setIsSubmitting(true);
        setError(null);
        try {
            // Once confirmSetup has succeeded, the SetupIntent is consumed on Stripe's side —
            // calling confirmSetup again on a retry throws setup_intent_unexpected_state. Reuse
            // the payment method from the first successful confirm instead of re-confirming.
            let paymentMethodId = confirmedPaymentMethodId;

            if (!paymentMethodId) {
                // Persist before confirmSetup, not after: if this issuer requires an off-page 3DS
                // challenge, the browser navigates away right inside this call and nothing after it
                // runs. See pendingBookingStorage.ts / the resume effect above for how this is used.
                persistPendingBooking(payload, captchaToken, setupIntentInfo.stripeCustomerId);

                const { error: confirmError, setupIntent } = await stripe.confirmSetup({
                    elements,
                    confirmParams: {
                        // Most cards don't need an off-page 3DS step for a setup — with
                        // redirect: "if_required" below, this URL is only ever visited
                        // in the rarer case Stripe does need to bounce the guest away.
                        return_url: window.location.href,
                    },
                    redirect: "if_required",
                });

                if (confirmError) {
                    setError(
                        confirmError.message ?? "We couldn’t save your card. Please try again.",
                    );
                    return;
                }

                if (!setupIntent || typeof setupIntent.payment_method !== "string") {
                    setError("We couldn’t save your card. Please try again.");
                    return;
                }

                paymentMethodId = setupIntent.payment_method;
                setConfirmedPaymentMethodId(paymentMethodId);
            }

            const result = await saveBookingByGuestFn({
                data: {
                    payload: {
                        ...payload,
                        stripeCustomerId: setupIntentInfo.stripeCustomerId,
                        stripePaymentMethodId: paymentMethodId,
                        stripeSetupIntentId: setupIntentInfo.stripeSetupIntentId,
                    },
                    captchaToken,
                },
            });

            if (isBookingNotAvailableError(result)) {
                setError(
                    `Sorry, the booking is not available at the selected time for ${totalGuests(payload)} guests. Please go back and try another time.`,
                );
                return;
            }

            if (isUnknownError(result)) {
                setError(result?.message || `Apologies, the booking couldn’t be saved.`);
                return;
            }

            onSuccess(result.isAutoConfirmed);
        } catch (err) {
            Sentry.captureException(err);
            console.error(JSON.stringify(err));
            setError(`Apologies, the booking couldn’t be saved due to an error.`);
        } finally {
            // If we got here, confirmSetup resolved without navigating away — no redirect is in
            // flight, so the persisted payload is no longer needed (a retry re-persists it).
            clearPendingBooking();
            setIsSubmitting(false);
        }
    };

    return (
        <Box
            component={Card}
            sx={{
                p: { xs: 2, sm: 4 },
                borderRadius: 2,
                boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
                border: "none",
                bgcolor: "white",
            }}
        >
            <Button
                onClick={onBack}
                startIcon={<ArrowBackIcon />}
                sx={{ mb: 3, textTransform: "none", color: "text.secondary" }}
            >
                Back to summary
            </Button>

            <Typography variant="h4" sx={{ mb: 3, fontWeight: 700 }}>
                Secure your booking
            </Typography>

            <Alert severity="info" sx={{ mb: 3 }}>
                This restaurant charges a cancellation fee of ${feeAmountDollars} if you cancel
                within {freeCancellationUntilHours}h of your booking, or don&apos;t show up. Your
                card will be saved but not charged now.
            </Alert>

            <Box component="form" onSubmit={handleSubmit}>
                <Box sx={{ mb: 3 }}>
                    <PaymentElement />
                </Box>

                {error && (
                    <Typography color="error" sx={{ mb: 3, fontWeight: 500 }}>
                        {error}
                    </Typography>
                )}

                <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    size="large"
                    disabled={!stripe || !elements || isSubmitting}
                >
                    {isSubmitting ? "Saving card..." : "Confirm & Save Card"}
                </Button>
            </Box>
        </Box>
    );
};
